import { useUserStore } from '@/stores';

export type ChatLanguage =
  | 'en' // English
  | 'es' // Spanish
  | 'fr' // French
  | 'de' // German
  | 'it' // Italian
  | 'pt' // Portuguese
  | 'zh' // Chinese (Simplified)
  | 'ja' // Japanese
  | 'ko' // Korean
  | 'ru' // Russian
  | 'ar' // Arabic
  | 'hi' // Hindi
  | 'nl' // Dutch
  | 'pl' // Polish
  | 'tr' // Turkish
  | 'vi' // Vietnamese
  | 'th' // Thai
  | 'sv' // Swedish
  | 'da' // Danish
  | 'fi' // Finnish
  | 'no' // Norwegian
  | 'he' // Hebrew
  | 'id' // Indonesian
  | 'ms' // Malay
  | 'uk' // Ukrainian
  | 'cs' // Czech
  | 'ro' // Romanian
  | 'hu' // Hungarian
  | 'el' // Greek
  | 'bn' // Bengali
  | 'ta' // Tamil
  | 'te' // Telugu
  | 'mr' // Marathi
  | 'ur' // Urdu
  | 'fa' // Farsi/Persian
  | 'sw' // Swahili
  | 'af' // Afrikaans
  | 'sq' // Albanian
  | 'am' // Amharic
  | 'hy' // Armenian
  | 'az' // Azerbaijani
  | 'eu' // Basque
  | 'be' // Belarusian
  | 'bs' // Bosnian
  | 'bg' // Bulgarian
  | 'ca' // Catalan
  | 'hr' // Croatian
  | 'et' // Estonian
  | 'ka' // Georgian
  | 'gu' // Gujarati
  | 'ht' // Haitian Creole
  | 'ha' // Hausa
  | 'is' // Icelandic
  | 'ga' // Irish
  | 'kn' // Kannada
  | 'kk' // Kazakh
  | 'lv' // Latvian
  | 'lt' // Lithuanian
  | 'mk' // Macedonian
  | 'ml' // Malayalam
  | 'mt' // Maltese
  | 'mn' // Mongolian
  | 'ne' // Nepali
  | 'pa' // Panjabi/Punjabi
  | 'ps' // Pashto
  | 'si' // Sinhala
  | 'sk' // Slovak
  | 'sl' // Slovenian
  | 'so' // Somali
  | 'sr' // Serbian
  | 'tl' // Tagalog
  | 'uz' // Uzbek
  | 'cy' // Welsh
  | 'zu'; // Zulu

export interface LanguageOption {
  value: ChatLanguage;
  label: string;
  nativeName?: string;
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish', nativeName: 'Español' },
  { value: 'fr', label: 'French', nativeName: 'Français' },
  { value: 'de', label: 'German', nativeName: 'Deutsch' },
  { value: 'it', label: 'Italian', nativeName: 'Italiano' },
  { value: 'pt', label: 'Portuguese', nativeName: 'Português' },
  { value: 'zh', label: 'Chinese (Simplified)', nativeName: '中文' },
  { value: 'ja', label: 'Japanese', nativeName: '日本語' },
  { value: 'ko', label: 'Korean', nativeName: '한국어' },
  { value: 'ru', label: 'Russian', nativeName: 'Русский' },
  { value: 'ar', label: 'Arabic', nativeName: 'العربية' },
  { value: 'hi', label: 'Hindi', nativeName: 'हिन्दी' },
  { value: 'nl', label: 'Dutch', nativeName: 'Nederlands' },
  { value: 'pl', label: 'Polish', nativeName: 'Polski' },
  { value: 'tr', label: 'Turkish', nativeName: 'Türkçe' },
  { value: 'vi', label: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { value: 'th', label: 'Thai', nativeName: 'ไทย' },
  { value: 'sv', label: 'Swedish', nativeName: 'Svenska' },
  { value: 'da', label: 'Danish', nativeName: 'Dansk' },
  { value: 'fi', label: 'Finnish', nativeName: 'Suomi' },
  { value: 'no', label: 'Norwegian', nativeName: 'Norsk' },
  { value: 'he', label: 'Hebrew', nativeName: 'עברית' },
  { value: 'id', label: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { value: 'ms', label: 'Malay', nativeName: 'Bahasa Melayu' },
  { value: 'uk', label: 'Ukrainian', nativeName: 'Українська' },
  { value: 'cs', label: 'Czech', nativeName: 'Čeština' },
  { value: 'ro', label: 'Romanian', nativeName: 'Română' },
  { value: 'hu', label: 'Hungarian', nativeName: 'Magyar' },
  { value: 'el', label: 'Greek', nativeName: 'Ελληνικά' },
  { value: 'bn', label: 'Bengali', nativeName: 'বাংলা' },
  { value: 'ta', label: 'Tamil', nativeName: 'தமிழ்' },
  { value: 'te', label: 'Telugu', nativeName: 'తెలుగు' },
  { value: 'mr', label: 'Marathi', nativeName: 'मराठी' },
  { value: 'ur', label: 'Urdu', nativeName: 'اردو' },
  { value: 'fa', label: 'Farsi/Persian', nativeName: 'فارسی' },
  { value: 'sw', label: 'Swahili', nativeName: 'Kiswahili' },
  { value: 'af', label: 'Afrikaans' },
  { value: 'sq', label: 'Albanian', nativeName: 'Shqip' },
  { value: 'am', label: 'Amharic', nativeName: 'አማርኛ' },
  { value: 'hy', label: 'Armenian', nativeName: 'Հայերեն' },
  { value: 'az', label: 'Azerbaijani', nativeName: 'Azərbaycan' },
  { value: 'eu', label: 'Basque', nativeName: 'Euskara' },
  { value: 'be', label: 'Belarusian', nativeName: 'Беларуская' },
  { value: 'bs', label: 'Bosnian', nativeName: 'Bosanski' },
  { value: 'bg', label: 'Bulgarian', nativeName: 'Български' },
  { value: 'ca', label: 'Catalan', nativeName: 'Català' },
  { value: 'hr', label: 'Croatian', nativeName: 'Hrvatski' },
  { value: 'et', label: 'Estonian', nativeName: 'Eesti' },
  { value: 'ka', label: 'Georgian', nativeName: 'ქართული' },
  { value: 'gu', label: 'Gujarati', nativeName: 'ગુજરાતી' },
  { value: 'ht', label: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen' },
  { value: 'ha', label: 'Hausa' },
  { value: 'is', label: 'Icelandic', nativeName: 'Íslenska' },
  { value: 'ga', label: 'Irish', nativeName: 'Gaeilge' },
  { value: 'kn', label: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { value: 'kk', label: 'Kazakh', nativeName: 'Қазақ' },
  { value: 'lv', label: 'Latvian', nativeName: 'Latviešu' },
  { value: 'lt', label: 'Lithuanian', nativeName: 'Lietuvių' },
  { value: 'mk', label: 'Macedonian', nativeName: 'Македонски' },
  { value: 'ml', label: 'Malayalam', nativeName: 'മലയാളം' },
  { value: 'mt', label: 'Maltese', nativeName: 'Malti' },
  { value: 'mn', label: 'Mongolian', nativeName: 'Монгол' },
  { value: 'ne', label: 'Nepali', nativeName: 'नेपाली' },
  { value: 'pa', label: 'Panjabi/Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { value: 'ps', label: 'Pashto', nativeName: 'پښتو' },
  { value: 'si', label: 'Sinhala', nativeName: 'සිංහල' },
  { value: 'sk', label: 'Slovak', nativeName: 'Slovenčina' },
  { value: 'sl', label: 'Slovenian', nativeName: 'Slovenščina' },
  { value: 'so', label: 'Somali', nativeName: 'Soomaali' },
  { value: 'sr', label: 'Serbian', nativeName: 'Српски' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'uz', label: 'Uzbek', nativeName: 'Oʻzbek' },
  { value: 'cy', label: 'Welsh', nativeName: 'Cymraeg' },
  { value: 'zu', label: 'Zulu', nativeName: 'isiZulu' },
];

export function useChatLanguage() {
  const chatLanguage = useUserStore(state =>
    (state.userSettings.chatLanguage as ChatLanguage) || 'en'
  );
  const updateUserSettings = useUserStore(state => state.updateUserSettings);

  const setChatLanguage = (value: ChatLanguage) => {
    updateUserSettings('chatLanguage', value);
  };

  const getLanguageLabel = (): string => {
    const option = LANGUAGE_OPTIONS.find(o => o.value === chatLanguage);
    return option?.label || 'English';
  };

  return { chatLanguage, setChatLanguage, getLanguageLabel };
}
